'use client';
import { FC, useState } from 'react';

import { Button, Text } from '@/components';
import { ModalPropTypes } from '@/layouts/Modal';
import { ProductItemPropTypes } from '@/components/Card/Product';
import { useWishlist } from '@/context/wishlistContext';
import { useCart } from '@/context/cartContext';
import { isStringifiedJSON } from '@/utils/helper';
import { useLoader } from '@/context/loaderContext';

interface GreetingCardData {
  greeting_card?: string;
}

const ShelfItem: FC<ModalPropTypes & ProductItemPropTypes> = ({
  onClose,
  item_id,
  quote_id,
  sku,
  qty,
  name,
  ...rest
}) => {
  const { addToWishlist } = useWishlist();
  const { removeFromCart, addToCartProduct, cartItems } = useCart();

  const { loading, setLoading } = useLoader();
  const [buttonLoading, setButtonLoading] = useState(false);

  async function removeAllLinkedGreetingCards(
    stringifiedData: string
  ): Promise<void> {
    const data: GreetingCardData | GreetingCardData[] =
      JSON.parse(stringifiedData);

    const processCartItem = async (
      cartGreetingCard: ProductItemPropTypes,
      quantity: number
    ) => {
      if (cartGreetingCard.qty === quantity) {
        await removeFromCart(cartGreetingCard);
      } else if (cartGreetingCard.qty && cartGreetingCard.qty > quantity) {
        await addToCartProduct(
          {
            ...cartGreetingCard,
            qty: cartGreetingCard.qty - quantity,
          },
          'put',
          false,
          { showToast: false }
        );
      }
    };

    if (data && !Array.isArray(data)) {
      const greetingCard = data?.greeting_card;
      if (greetingCard) {
        const cartGreetingCard = cartItems?.find(
          item => item.sku === greetingCard
        );
        if (cartGreetingCard) {
          await processCartItem(cartGreetingCard, 1);
        }
      }
    } else {
      const greetingCardsInItem: Record<string, number> = {};
      data.forEach(el => {
        const greetingCard = el?.greeting_card;
        if (greetingCard) {
          greetingCardsInItem[greetingCard] =
            (greetingCardsInItem[greetingCard] || 0) + 1;
        }
      });

      for (const [sku, qty] of Object.entries(greetingCardsInItem)) {
        const cartGreetingCard = cartItems?.find(item => item.sku === sku);
        if (cartGreetingCard) {
          await processCartItem(cartGreetingCard, qty);
        }
      }
    }
  }

  const onShelf = async () => {
    setButtonLoading(true);
    const isItemMoved = await addToWishlist({ sku, name });
    if (isItemMoved) {
      if (
        rest?.extension_attributes?.is_gift_hamper === '1' &&
        rest?.product_option?.extension_attributes?.custom_options?.length >
          1 &&
        isStringifiedJSON(
          rest?.product_option?.extension_attributes?.custom_options[1]
            ?.option_value
        )
      ) {
        setLoading(true);
        setButtonLoading(true);
        await removeAllLinkedGreetingCards(
          rest?.product_option?.extension_attributes?.custom_options[1]
            ?.option_value
        );
        setLoading(false);
        setButtonLoading(false);
      }
      await removeFromCart({ item_id, quote_id, sku, qty, name });
      setButtonLoading(false);
      onClose();
    }
  };

  return (
    <>
      <div aria-label='shelf-item' className='space-y-6'>
        <Text className='p-0'>
          Are you sure you want to move “{name}” from cart to shelf?
        </Text>

        <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
          <Button
            label='Move to Shelf'
            onClick={onShelf}
            className='max-md:py-[0.94rem] disabled:bg-fig'
            isLoading={buttonLoading}
            disabled={buttonLoading}
          />

          <Button
            onClick={onClose}
            disabled={loading}
            label='No, Keep in Cart'
            variant='secondary'
            className='max-md:border-0 max-md:py-[0.94rem] max-md:underline'
          />
        </div>
      </div>
    </>
  );
};

export default ShelfItem;
